/**
 * Which microphone and camera a meeting uses (board m5gc #11).
 *
 * RealtimeKit keeps its own device preference in localStorage
 * (`Realtimekit::<handler>::UserDeviceID`) and falls back to it whenever we
 * do not say otherwise. The pre-join screen used to show the first device in
 * the list while only passing a choice on when the user actively changed
 * it — so an untouched screen showed microphone A and the meeting published
 * microphone B from last week. The rule now: the ids the screen shows are
 * always resolved explicitly here, and those exact ids are what gets set on
 * RealtimeKit at join. The choice is remembered for the browser session so
 * a rejoin and the in-call switcher agree with it.
 */

export type DeviceKind = 'audio' | 'video'

export interface DeviceChoice {
  audioDeviceId?: string
  videoDeviceId?: string
}

/** A device the browser lets us address — a refused one has an empty id. */
export function usableDevices(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
  return devices.filter((device) => device.deviceId)
}

/**
 * The device to show and use for one kind, in order of intent: what the user
 * picked on this screen, what they used earlier this session, what the
 * preview stream is already capturing, else the first usable device. Never
 * an id that is not in the list — the browser dropped that device.
 */
export function resolveDeviceId(input: {
  chosen: string | undefined
  remembered: string | undefined
  preview: string | null | undefined
  devices: MediaDeviceInfo[]
}): string | undefined {
  const ids = new Set(usableDevices(input.devices).map((device) => device.deviceId))
  for (const candidate of [input.chosen, input.remembered, input.preview ?? undefined]) {
    if (candidate && ids.has(candidate)) return candidate
  }
  return ids.values().next().value
}

export function resolveDeviceChoice(input: {
  chosen: DeviceChoice
  remembered: DeviceChoice
  preview: { audio: string | null; video: string | null }
  audioDevices: MediaDeviceInfo[]
  videoDevices: MediaDeviceInfo[]
}): DeviceChoice {
  return {
    audioDeviceId: resolveDeviceId({
      chosen: input.chosen.audioDeviceId,
      remembered: input.remembered.audioDeviceId,
      preview: input.preview.audio,
      devices: input.audioDevices,
    }),
    videoDeviceId: resolveDeviceId({
      chosen: input.chosen.videoDeviceId,
      remembered: input.remembered.videoDeviceId,
      preview: input.preview.video,
      devices: input.videoDevices,
    }),
  }
}

/** The device id a live track is capturing from, if the browser tells us. */
export function trackDeviceId(track: MediaStreamTrack | null | undefined): string | null {
  const id = track?.getSettings?.().deviceId
  return typeof id === 'string' && id ? id : null
}

// ── Session memory ────────────────────────────────────────────────────────

const STORAGE_KEY = 'hub:meet:devices'

export function readRememberedDevices(): DeviceChoice {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Partial<DeviceChoice>
    return {
      audioDeviceId: typeof parsed.audioDeviceId === 'string' ? parsed.audioDeviceId : undefined,
      videoDeviceId: typeof parsed.videoDeviceId === 'string' ? parsed.videoDeviceId : undefined,
    }
  } catch {
    return {}
  }
}

export function rememberDevice(kind: DeviceKind, deviceId: string): void {
  try {
    const next = { ...readRememberedDevices() }
    if (kind === 'audio') next.audioDeviceId = deviceId
    else next.videoDeviceId = deviceId
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Private mode or a full store: the choice simply is not remembered.
  }
}
